import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import MaterialEntryForm from "../../components/materialLines/MaterialEntryForm";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import { cardClass, pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import {
  clearCurrentMaterialPurchase,
  clearMaterialPurchasePartyData,
  fetchMaterialPurchaseDetail,
  fetchMaterialPurchaseMaterials,
  fetchMaterialPurchaseOpenOrders,
  fetchMaterialPurchaseParties,
  fetchMaterialPurchasePartyInfo,
  getCurrentMaterialPurchase,
  getCurrentMaterialPurchaseLoading,
  getMaterialPurchaseMaterials,
  getMaterialPurchaseMaterialsLoading,
  getMaterialPurchaseOpenOrders,
  getMaterialPurchaseOpenOrdersLoading,
  getMaterialPurchasePartyInfo,
  getMaterialPurchasePartyOptions,
} from "../../features/materialPurchases/materialPurchasesSlice";
import { PUT } from "../../services/axios.service";
import {
  costPayload,
  emptyLine,
  linePayload,
  priceText,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { IMaterialLinesFormInput } from "../../types/materialLine";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "મટીરીયલ ખરીદી", href: route.materialPurchases },
  { label: "ખરીદી સુધારો" },
];

const MaterialPurchaseEditPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const currentPurchase = useAppSelector(getCurrentMaterialPurchase);
  const currentPurchaseLoading = useAppSelector(
    getCurrentMaterialPurchaseLoading
  );
  const partyOptions = useAppSelector(getMaterialPurchasePartyOptions);
  const materials = useAppSelector(getMaterialPurchaseMaterials);
  const materialsLoading = useAppSelector(getMaterialPurchaseMaterialsLoading);
  const partyInfo = useAppSelector(getMaterialPurchasePartyInfo);
  const openOrders = useAppSelector(getMaterialPurchaseOpenOrders);
  const openOrdersLoading = useAppSelector(getMaterialPurchaseOpenOrdersLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  // Without it the rates stay as they are, and the lines added wait for a rate at 0. The additional costs are amounts
  // too: without it they aren't shown, and the purchase keeps them as they are.
  const canRate = hasPermission(
    loggingUser?.permissions,
    permission.materialPurchasesRate
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const purchaseId = currentPurchase?.id;
  const purchasePartyId = currentPurchase?.party_id;

  useEffect(() => {
    if (id) {
      dispatch(fetchMaterialPurchaseDetail(Number(id)));
      dispatch(fetchMaterialPurchaseMaterials());
    }
    return () => {
      dispatch(clearCurrentMaterialPurchase());
      dispatch(clearMaterialPurchasePartyData());
    };
  }, [id, dispatch]);

  // This purchase's own qty and prices don't count as received or as the party's last prices, and the orders it has
  // lines of stay on the list even when closed since.
  const loadPartyData = useCallback(
    (partyId: number) => {
      if (partyId) {
        const request = { party_id: partyId, purchase_id: Number(id) };

        dispatch(fetchMaterialPurchasePartyInfo(request));
        dispatch(fetchMaterialPurchaseOpenOrders(request));
      } else {
        dispatch(clearMaterialPurchasePartyData());
      }
    },
    [id, dispatch]
  );

  useEffect(() => {
    if (purchaseId && purchasePartyId) {
      dispatch(fetchMaterialPurchaseParties(purchasePartyId));
      loadPartyData(purchasePartyId);
    }
  }, [purchaseId, purchasePartyId, dispatch, loadPartyData]);

  const handleSubmit = async (data: IMaterialLinesFormInput) => {
    setIsSubmitting(true);
    try {
      await PUT(`/v1/admin/material-purchases/${id}`, {
        party_id: data.party_id,
        date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
        items: linePayload(data.items, canRate),
        ...(canRate
          ? { additional_costs: costPayload(data.additional_costs) }
          : {}),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.materialPurchases);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // A line still waiting for its rate starts with an empty rate box, so whoever may enter rates has to fill it in.
  const defaultValues = useMemo((): IMaterialLinesFormInput | undefined => {
    if (!currentPurchase) return undefined;
    return {
      party_id: currentPurchase.party_id,
      date: moment(currentPurchase.date).format("YYYY-MM-DDTHH:mm"),
      items:
        currentPurchase.items && currentPurchase.items.length > 0
          ? currentPurchase.items.map((item) => ({
              material_id: item.material_id,
              material_size_id: item.material_size_id,
              order_item_id: item.order_item_id || 0,
              order_id: item.order_item?.order_id || 0,
              close_order: !!item.close_order,
              qty: String(item.qty),
              price:
                canRate && !item.rate_pending ? priceText(item.price) : "",
            }))
          : [{ ...emptyLine }],
      additional_costs: canRate
        ? (currentPurchase.additional_costs || []).map((cost) => ({
            note: cost.note,
            amount: priceText(cost.amount),
          }))
        : [],
    };
  }, [currentPurchase, canRate]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>મટીરીયલ ખરીદી સુધારો</h1>
      </div>

      <div className="flex flex-col gap-6">
        {currentPurchaseLoading ? (
          <div className={cx(cardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : currentPurchase && defaultValues ? (
          <MaterialEntryForm
            key={currentPurchase.id}
            defaultValues={defaultValues}
            partyOptions={partyOptions}
            materials={materials}
            materialsLoading={materialsLoading}
            partyInfo={partyInfo}
            onPartyChange={loadPartyData}
            isPurchase
            withPrice={canRate}
            openOrders={openOrders}
            openOrdersLoading={openOrdersLoading}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCancel={() => navigate(route.materialPurchases)}
            submitLabel="સાચવો"
          />
        ) : (
          <div className={cx(cardClass, "p-8 text-center text-gray-500")}>
            મટીરીયલ ખરીદી ની વિગતો મળી નથી
          </div>
        )}
      </div>
    </AppLayout>
  );
};
export default MaterialPurchaseEditPage;
